import { OptionType } from '@evinced-private/ui-common';

import {
	extractLabelKey,
	parseLabel
} from 'src/apps/web-sdks/pages/tests/components/shared/helpers';
import { IWebSdkTest } from 'src/apps/web-sdks/types/WebSdkDashboardServiceTypes';
import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

import {
	createCustomFieldAddChange,
	createCustomFieldRemoveChange
} from '../builders/PayloadBuilder';
import { createRemovalChangeForLabel } from '../helpers/LabelRemovalHelpers';
import { Change } from '../types/PayloadTypes';

const buildLabelMap = (configuredLabels: ConfigurationLabel[] = []): Map<string, string> => {
	const labelMap = new Map<string, string>();
	configuredLabels.forEach((label) => {
		labelMap.set(label.label.toLowerCase(), label.label);
	});
	return labelMap;
};

const getConfiguredKey = (labelKey: string, labelMap: Map<string, string>): string => {
	const lowerKey = labelKey.toLowerCase();
	return labelMap.get(lowerKey) || labelKey;
};

const transformAddition = (
	labelType: OptionType,
	value: OptionType,
	labelMap: Map<string, string>
): Change => {
	const configuredKey = getConfiguredKey(labelType.label, labelMap);
	return createCustomFieldAddChange(configuredKey, value.label);
};

export const transformAdditionsForRow = (
	row: IWebSdkTest,
	additions: Array<{ labelType: OptionType; value: OptionType }>,
	configuredLabels: ConfigurationLabel[] = []
): Change[] => {
	if (!additions.length) {
		return [];
	}

	const labelMap = buildLabelMap(configuredLabels);
	const existingKeys = new Set((row?.labels ?? []).map((label: string) => extractLabelKey(label)));

	return additions
		.filter((addition) => !existingKeys.has(addition.labelType.label.toLowerCase()))
		.map((addition) => transformAddition(addition.labelType, addition.value, labelMap));
};

const transformRemoval = (labelString: string): Change | null => {
	const parsed = parseLabel(labelString);
	if (!parsed) {
		return null;
	}

	return createCustomFieldRemoveChange(parsed.key, parsed.value);
};

const transformRemovalForRow = (labelString: string, row: IWebSdkTest): Change | null => {
	return createRemovalChangeForLabel(labelString, row);
};

export const transformRemovals = (removals: string[]): Change[] => {
	const changes = removals.map((removal) => transformRemoval(removal));
	return changes.filter((change): change is Change => change !== null);
};

export const transformRemovalsForRow = (removals: string[], row: IWebSdkTest): Change[] => {
	const changes = removals.map((removal) => transformRemovalForRow(removal, row));
	return changes.filter((change): change is Change => change !== null);
};
